/**
 * Formatting shared by the panel's rows, kept apart from the components so it is unit-testable
 * without rendering.
 */

import dayjs from 'dayjs';
import { Appointment } from '@medplum/fhirtypes';
import { PN_CONSULTATION_CODE } from '@bariendo/lib/constants';
import { CallRowStatus } from '@bariendo/lib/intake-panel';

/** The panel's only date format. */
const PANEL_DATE_FORMAT = 'MMM D, YYYY';

/**
 * **Always carries the year.** These panels are read a year later during
 * win-back, and a lead who signed up in March and consulted in August is
 * ordinary — a bare `Aug 3` silently assumes "this year", which is wrong
 * exactly when it matters.
 */
export function formatPanelDate(date: string | undefined): string {
  return date ? dayjs(date).format(PANEL_DATE_FORMAT) : '';
}

/**
 * `Questionnaire/next-step` is multi-select, so a consult can carry several
 * answers. The first plus a count keeps the row inside the 3/9 grid; the full
 * set is on the encounter note.
 */
export function formatConsultOutcome(answers: string[]): string {
  if (answers.length === 0) return '';

  const [first, ...rest] = answers;

  return rest.length > 0 ? `${first} +${rest.length}` : first;
}

/**
 * The outcome of the consult the Consult row dates. A PN consult records it on the PN card
 * (`pnOutcome`); the NP medical consult on the `next-step` form. Taking the form's answer for a PN
 * consult would show an older NP consult's result, or "Occurred" for every Intake 1.1 patient.
 */
export function consultOutcome(
  consults: Appointment[],
  status: CallRowStatus,
  { pnOutcome, nextStepAnswers }: { pnOutcome?: string; nextStepAnswers: string[] }
): string {
  if (status.kind !== 'occurred') return '';

  const dated = consults.find((consult) => (consult.start ?? consult.end) === status.date);
  const isPn = dated?.appointmentType?.coding?.some((coding) => coding.code === PN_CONSULTATION_CODE);

  return isPn ? (pnOutcome ?? '') : formatConsultOutcome(nextStepAnswers);
}
